/**
 * KAN-862: колонка канбан-доски CRM: заголовок со счётчиком, карточки с
 * вертикальной прокруткой внутри колонки.
 * Свёрнутая колонка (карточки не пришли с доской): счётчик и "Показать",
 * затем карточки порциями по 50 и "Загрузить ещё". Колонка, у которой
 * клиентов больше, чем карточек в ответе доски, тоже дозагружается порциями.
 * dnd: колонка - зона переноса карточек (desktop).
 * KAN-865: onOpen открывает детали клиента (и у карточек подгруженного этапа),
 * attentionOnly уходит в запрос порций этапа. fill: колонка на всю высоту
 * доски (h-full), список прокручивается внутри без своего max-height.
 * KAN-880: порции колонки ?column=<key> с фильтрами доски; у взятых в работу
 * дни в работе вместо дней на этапе.
 * KAN-882: порции ?board=<view>&column=<key>; колонка принимает перенос по
 * accepts (acceptsDrop), карточка тянется, если canDrag (лид открыт и есть
 * куда переносить); showState - метка состояния на карточках.
 */
import type { CrmRange } from '@/lib/crmTable';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { Loader2 } from 'lucide-react';
import { useCrmColumnCards } from '@/hooks/useCrmPipeline';
import { acceptsDrop } from '@/lib/crmPipeline';
import { cn } from '@/lib/utils';
import type { CrmBoardColumn, CrmBoardView, CrmLeadFilterParams, CrmPipelineCard } from '@/types/crmPipeline';
import { KanbanCard, type KanbanCardProps } from './KanbanCard';

export interface KanbanCardLinks {
  clientHref: (card: CrmPipelineCard) => string;
  /** Ссылка на организацию клиента (у организации с id). */
  companyHref?: (companyId: string) => string;
  /** Кнопка "Сменить этап" в карточке (телефон). */
  onChangeStage?: (card: CrmPipelineCard) => void;
  /** KAN-865: открыть детали клиента по клику на карточку. */
  onOpen?: (card: CrmPipelineCard) => void;
  /** KAN-882: метка состояния на карточке (виды managers и funnel). */
  showState?: boolean;
}

interface KanbanColumnProps extends KanbanCardLinks {
  board: CrmBoardView;
  column: CrmBoardColumn;
  activity: CrmRange | null;
  /** KAN-865: только требующие внимания (порции этапа с ?attention=1). */
  attentionOnly?: boolean;
  /** KAN-880: фильтры доски для порций колонки. */
  filters?: CrmLeadFilterParams;
  variant: 'desktop' | 'mobile';
  /** KAN-865: высоту задаёт доска: колонка тянется, список прокручивается внутри. */
  fill?: boolean;
  /** Колонка - зона переноса, карточки перетаскиваются (desktop). */
  dnd: boolean;
  /** KAN-882: карточку можно тянуть; нет: можно все. */
  canDrag?: (card: CrmPipelineCard) => boolean;
  className?: string;
}

const LINK_BUTTON = 'w-full rounded-md py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-60';

export function KanbanColumn({
  board,
  column,
  activity,
  attentionOnly = false,
  filters,
  variant,
  fill = false,
  dnd,
  canDrag,
  clientHref,
  companyHref,
  onChangeStage,
  onOpen,
  showState = false,
  className,
}: KanbanColumnProps): JSX.Element {
  const [paging, setPaging] = useState(false);
  const stage = useCrmColumnCards(board, paging ? column.key : null, activity, attentionOnly, filters);
  const hidden = column.collapsed && !paging;
  const cards = paging ? (stage.data?.pages.flatMap((p) => p.cards) ?? []) : column.cards;
  const canPage = !paging && !column.collapsed && column.count > column.cards.length;

  const cardProps = (card: CrmPipelineCard): KanbanCardProps => ({
    card,
    href: clientHref(card),
    companyHref: card.company?.id && companyHref ? companyHref(card.company.id) : null,
    onChangeStage,
    onOpen,
    showState,
  });

  let footer: ReactNode = null;
  if (hidden) {
    footer = (
      <button type="button" className={LINK_BUTTON} onClick={() => setPaging(true)}>
        Показать
      </button>
    );
  } else if (paging && stage.isLoading) {
    footer = (
      <div className="flex justify-center py-2">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" aria-label="Загрузка" />
      </div>
    );
  } else if (paging && stage.isError) {
    footer = <p className="py-2 text-center text-xs text-red-600">Не удалось загрузить карточки</p>;
  } else if ((paging && stage.hasNextPage) || canPage) {
    footer = (
      <button
        type="button"
        className={LINK_BUTTON}
        disabled={stage.isFetchingNextPage}
        onClick={() => (paging ? void stage.fetchNextPage() : setPaging(true))}
      >
        {stage.isFetchingNextPage ? 'Загружаем...' : 'Загрузить ещё'}
      </button>
    );
  }

  const listClass = (over = false) =>
    cn(
      'flex min-h-[3rem] flex-1 flex-col gap-2 overflow-y-auto p-2',
      fill ? 'min-h-0' : variant === 'mobile' ? 'max-h-[calc(100dvh-17rem)]' : 'max-h-[calc(100vh-15rem)]',
      over && 'bg-blue-50'
    );
  const visible = hidden ? [] : cards;

  return (
    <section
      aria-label={column.title}
      data-column-key={column.key}
      className={cn('flex shrink-0 flex-col rounded-xl border border-gray-200 bg-gray-50', fill && 'h-full min-h-0', className)}
    >
      <header className="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">
        <h3 className="truncate text-sm font-semibold text-gray-800">{column.title}</h3>
        <span
          data-testid="kanban-column-count"
          className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-600"
        >
          {column.count}
        </span>
      </header>
      {dnd ? (
        <Droppable droppableId={column.key} isDropDisabled={!acceptsDrop(column)}>
          {(drop, snapshot) => (
            <div ref={drop.innerRef} {...drop.droppableProps} className={listClass(snapshot.isDraggingOver)}>
              {visible.map((card, index) => (
                <Draggable key={card.id} draggableId={card.id} index={index} isDragDisabled={canDrag ? !canDrag(card) : false}>
                  {(drag, dragSnapshot) => (
                    <KanbanCard
                      ref={drag.innerRef}
                      {...drag.draggableProps}
                      {...drag.dragHandleProps}
                      style={drag.draggableProps.style as CSSProperties}
                      {...cardProps(card)}
                      dragging={dragSnapshot.isDragging}
                    />
                  )}
                </Draggable>
              ))}
              {drop.placeholder}
              {footer}
            </div>
          )}
        </Droppable>
      ) : (
        <div className={listClass()}>
          {visible.map((card) => (
            <KanbanCard key={card.id} {...cardProps(card)} />
          ))}
          {footer}
        </div>
      )}
    </section>
  );
}
